function asString(v, fallback = '') {
  return typeof v === 'string' && v ? v : fallback;
}

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function asObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
}

function pickFirstObject(v) {
  if (asObject(v)) return v;

  const arr = asArray(v);
  if (arr.length && asObject(arr[0])) return arr[0];

  return {};
}

function extractModulesList(payload) {
  const obj = pickFirstObject(payload);

  const direct = asArray(obj?.modules);
  if (direct.length) return direct;

  const dataWrapped = asArray(obj?.data?.modules);
  if (dataWrapped.length) return dataWrapped;

  const blueprintWrapped = asArray(obj?.blueprint?.modules);
  if (blueprintWrapped.length) return blueprintWrapped;

  const arr = asArray(payload);
  if (arr.length && asObject(arr[0])) return arr;

  return [];
}

function normalizeTracks(moduleOverview) {
  const tracks = asArray(moduleOverview?.tracks)
    .map((t) => {
      const track_id = asString(t?.track_id);
      const rank = Number(t?.rank ?? 0);

      return track_id
        ? {
            track_id,
            rank,
            startLabel: asString(t?.startLabel),
            label: asString(t?.label),
          }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => Number(a.rank) - Number(b.rank));

  const default_track_id = asString(moduleOverview?.default_track_id);
  const fallbackDefault = tracks[0]?.track_id ?? '';

  return {
    track_policy: asString(moduleOverview?.track_policy, 'override'),
    default_track_id: default_track_id || fallbackDefault,
    tracks,
  };
}

function normalizeModuleId(m) {
  return asString(m?.module_id, asString(m?.id, ''));
}

function normalizeTrackId(v) {
  return asString(v, '');
}

function getRunOverviewPayload(runOverview) {
  const obj = pickFirstObject(runOverview);

  if (asObject(obj?.data)) {
    return obj.data;
  }

  return obj;
}

function buildRunOverviewMap(runOverview) {
  const ro = getRunOverviewPayload(runOverview);
  const rows = asArray(ro?.modules);

  return rows.reduce((acc, row) => {
    const module_id = normalizeModuleId(row);
    if (!module_id) return acc;

    const track_id = normalizeTrackId(row?.track_id);
    const exactKey = `${module_id}::${track_id || 'default'}`;

    acc.set(exactKey, row);

    if (!track_id && !acc.has(`${module_id}::__module__`)) {
      acc.set(`${module_id}::__module__`, row);
    }

    return acc;
  }, new Map());
}

function findRunOverviewForModule(runMap, module_id, default_track_id, tracks) {
  const defaultTrackId = normalizeTrackId(default_track_id);
  const candidates = [
    `${module_id}::${defaultTrackId || 'default'}`,
    ...asArray(tracks).map(
      (track) => `${module_id}::${normalizeTrackId(track?.track_id) || 'default'}`
    ),
    `${module_id}::__module__`,
    `${module_id}::default`,
  ];

  for (const key of candidates) {
    if (runMap.has(key)) {
      return runMap.get(key);
    }
  }

  return null;
}

function clonePopover(popover) {
  const p = asObject(popover);
  if (!p) return null;

  return {
    ...p,
    elements: asArray(p.elements)
      .map((item) => {
        if (!asObject(item)) return null;

        return {
          ...item,
          id: asString(item.id),
          label: Object.prototype.hasOwnProperty.call(item, 'label') ? asString(item.label) : '',
          value: asString(item.value),
          type: asString(item.type, 'text'),
          className: asString(item.className),
        };
      })
      .filter(Boolean),
  };
}

function getPopoverElements(runtime, ovm, baseModule) {
  const runtimeProgress = asObject(runtime?.progress);
  const ovmProgress = asObject(ovm?.progress);
  const baseProgress = asObject(baseModule?.progress);

  const runtimeModuleProgress = asObject(runtime?.moduleProgress);
  const ovmModuleProgress = asObject(ovm?.moduleProgress);
  const baseModuleProgress = asObject(baseModule?.moduleProgress);

  return (
    clonePopover(runtimeProgress?.popover) ||
    clonePopover(runtimeModuleProgress?.popover) ||
    clonePopover(ovmProgress?.popover) ||
    clonePopover(ovmModuleProgress?.popover) ||
    clonePopover(baseProgress?.popover) ||
    clonePopover(baseModuleProgress?.popover) ||
    null
  );
}

function getMetricFromPopover(popover, matcher) {
  const elements = asArray(popover?.elements);

  for (const item of elements) {
    if (!asObject(item)) continue;

    const id = asString(item.id).trim().toLowerCase();
    const label = asString(item.label).trim().toLowerCase();
    const value = asString(item.value).trim();

    if (!value) continue;
    if (matcher({ id, label, value, item })) return value;
  }

  return '';
}

function toIntFromMetric(value, fallback = 0) {
  const text = asString(value).trim();
  if (!text) return fallback;

  const match = text.match(/-?\d+/);
  if (!match) return fallback;

  const n = Number(match[0]);
  return Number.isFinite(n) ? n : fallback;
}

function toConfidenceFromPopover(popover) {
  const value = getMetricFromPopover(
    popover,
    ({ id, label, item }) =>
      id === 'confidence_level' ||
      id === 'grounding_status' ||
      label === 'confidence' ||
      label === 'grounding' ||
      asString(item?.className).includes('is-success') ||
      asString(item?.className).includes('is-strong')
  );

  return asString(value).trim();
}

function normalizeProgressData(runtime, ovm, baseModule) {
  const popover = getPopoverElements(runtime, ovm, baseModule);

  return {
    progress: popover
      ? {
          popover,
        }
      : null,
    moduleProgress: {
      enabled: Boolean(popover),
      popover,
    },
  };
}

function resolvePatternsIdentified(runtime, ovm, baseModule, popover) {
  const explicit =
    Number(
      runtime?.patterns_identified ??
        ovm?.patterns_identified ??
        baseModule?.patterns_identified ??
        baseModule?.insightsCount ??
        ovm?.insightsCount ??
        0
    ) || 0;

  if (explicit > 0) return explicit;

  return toIntFromMetric(
    getMetricFromPopover(
      popover,
      ({ id, label }) => id === 'patterns_identified' || label === 'patterns identified'
    ),
    0
  );
}

function resolveReportsAvailable(runtime, ovm, baseModule, popover) {
  const explicit =
    Number(
      runtime?.reports_available ??
        ovm?.reports_available ??
        baseModule?.reports_available ??
        baseModule?.reportsCount ??
        0
    ) || 0;

  if (explicit > 0) return explicit;

  return toIntFromMetric(
    getMetricFromPopover(
      popover,
      ({ id, label }) => id === 'reports_available' || label === 'reports available'
    ),
    0
  );
}

function resolveConfidenceLevel(runtime, ovm, baseModule, popover) {
  const explicit = asString(
    runtime?.confidence_level,
    asString(ovm?.confidence_level, asString(baseModule?.confidence_level, ''))
  ).trim();

  if (explicit) return explicit;

  return toConfidenceFromPopover(popover);
}

function normalizeCoachResolution(runtime, ovm, baseModule, rootOverview) {
  const resolution =
    asObject(runtime?.coach_resolution) ||
    asObject(ovm?.coach_resolution) ||
    asObject(baseModule?.coach_resolution) ||
    asObject(rootOverview?.coach_resolution) ||
    null;

  if (!resolution) {
    return null;
  }

  const active = asObject(resolution.active);

  return {
    requested:
      resolution.requested === null || resolution.requested === undefined
        ? null
        : asString(resolution.requested).trim() || null,
    active: active
      ? {
          id: asString(active.id).trim(),
          label: asString(active.label).trim(),
          source: asString(active.source).trim(),
        }
      : null,
    fallback_used: Boolean(resolution.fallback_used),
  };
}

function resolveTypingMode(runtime, ovm, baseModule, rootOverview) {
  return (
    asString(runtime?.typing_mode).trim() ||
    asString(ovm?.typing_mode).trim() ||
    asString(baseModule?.typing_mode).trim() ||
    asString(rootOverview?.typing_mode).trim() ||
    'fast'
  );
}

function buildModuleFromOverview(ovm, runtime = null, rootOverview = null) {
  const module_id = normalizeModuleId(ovm);
  const tracksInfo = normalizeTracks(ovm);
  const progressData = normalizeProgressData(runtime, ovm, null);
  const popover = progressData?.progress?.popover || progressData?.moduleProgress?.popover || null;

  const patternsIdentified = resolvePatternsIdentified(runtime, ovm, null, popover);
  const reportsAvailable = resolveReportsAvailable(runtime, ovm, null, popover);
  const confidenceLevel = resolveConfidenceLevel(runtime, ovm, null, popover);
  const coachResolution = normalizeCoachResolution(runtime, ovm, null, rootOverview);
  const typingMode = resolveTypingMode(runtime, ovm, null, rootOverview);

  return {
    id: module_id,
    title: asString(ovm?.title, module_id),
    description: asString(ovm?.description, ''),
    subtitle: asString(ovm?.subtitle, ''),
    startLabel: asString(ovm?.startLabel, ''),
    required: Boolean(ovm?.required ?? true),

    totalSteps:
      Number(runtime?.total_count ?? ovm?.total_count ?? ovm?.totalSteps ?? ovm?.stepsCount ?? 0) ||
      0,
    completedSteps:
      Number(runtime?.answered_count ?? ovm?.answered_count ?? ovm?.completedSteps ?? 0) || 0,
    insightsCount: patternsIdentified,
    reportsCount: reportsAvailable,
    confidence: confidenceLevel || asString(ovm?.confidence, '-'),
    status: asString(runtime?.status, asString(ovm?.status, 'not_started')),
    progress_ratio: Number(runtime?.progress_ratio ?? ovm?.progress_ratio ?? 0) || 0,
    answered_count: Number(runtime?.answered_count ?? ovm?.answered_count ?? 0) || 0,
    total_count:
      Number(runtime?.total_count ?? ovm?.total_count ?? ovm?.totalSteps ?? ovm?.stepsCount ?? 0) ||
      0,
    patterns_identified: patternsIdentified,
    reports_available: reportsAvailable,
    confidence_level: confidenceLevel,
    last_saved_at: asString(runtime?.last_saved_at, asString(ovm?.last_saved_at, '')),
    completed_at: asString(runtime?.completed_at, asString(ovm?.completed_at, '')),

    progress: progressData.progress,
    moduleProgress: progressData.moduleProgress,

    coach_resolution: coachResolution,
    typing_mode: typingMode,

    track_policy: tracksInfo.track_policy,
    default_track_id: tracksInfo.default_track_id,
    tracks: tracksInfo.tracks,

    content: [],
  };
}

export function adaptBlueprint({ packId, blueprint, modulesOverview, runOverview }) {
  const bp = pickFirstObject(blueprint);
  const ov = pickFirstObject(modulesOverview);
  const ro = getRunOverviewPayload(runOverview);

  const bpModules = extractModulesList(blueprint);
  const ovModules = extractModulesList(modulesOverview);
  const runMap = buildRunOverviewMap(runOverview);
  const globalAvailableCoaches = normalizeAvailableCoaches(ro);

  const ovById = new Map(
    ovModules
      .map((m) => {
        const module_id = normalizeModuleId(m);
        return module_id ? [module_id, m] : null;
      })
      .filter(Boolean)
  );

  const baseList = bpModules.length
    ? bpModules
    : ovModules.map((m) => ({
        module_id: normalizeModuleId(m),
        title: asString(m?.title, normalizeModuleId(m)),
        description: asString(m?.description, ''),
        subtitle: asString(m?.subtitle, ''),
        startLabel: asString(m?.startLabel, ''),
        required: Boolean(m?.required ?? true),
      }));

  const merged = baseList
    .map((m) => {
      const module_id = normalizeModuleId(m);
      if (!module_id) return null;

      const ovm = ovById.get(module_id) || {};
      const tracksInfo = normalizeTracks(ovm);
      const runtime = findRunOverviewForModule(
        runMap,
        module_id,
        tracksInfo.default_track_id,
        tracksInfo.tracks
      );
      const progressData = normalizeProgressData(runtime, ovm, m);
      const popover =
        progressData?.progress?.popover || progressData?.moduleProgress?.popover || null;

      const patternsIdentified = resolvePatternsIdentified(runtime, ovm, m, popover);
      const reportsAvailable = resolveReportsAvailable(runtime, ovm, m, popover);
      const confidenceLevel = resolveConfidenceLevel(runtime, ovm, m, popover);
      const coachResolution = normalizeCoachResolution(runtime, ovm, m, ro);
      const typingMode = resolveTypingMode(runtime, ovm, m, ro);

      return {
        id: module_id,
        title: asString(m?.title, asString(ovm?.title, module_id)),
        description: asString(m?.description, asString(ovm?.description, '')),
        subtitle: asString(m?.subtitle, asString(ovm?.subtitle, '')),
        startLabel: asString(m?.startLabel, asString(ovm?.startLabel, '')),
        required: Boolean(m?.required ?? ovm?.required ?? true),

        totalSteps:
          Number(
            runtime?.total_count ??
              ovm?.total_count ??
              m?.total_count ??
              m?.totalSteps ??
              m?.stepsCount ??
              ovm?.totalSteps ??
              ovm?.stepsCount ??
              0
          ) || 0,
        completedSteps:
          Number(
            runtime?.answered_count ??
              ovm?.answered_count ??
              m?.answered_count ??
              m?.completedSteps ??
              ovm?.completedSteps ??
              0
          ) || 0,
        insightsCount: patternsIdentified,
        reportsCount: reportsAvailable,
        confidence: confidenceLevel || asString(m?.confidence, asString(ovm?.confidence, '-')),
        status: asString(
          runtime?.status,
          asString(ovm?.status, asString(m?.status, 'not_started'))
        ),
        progress_ratio:
          Number(runtime?.progress_ratio ?? ovm?.progress_ratio ?? m?.progress_ratio ?? 0) || 0,
        answered_count:
          Number(runtime?.answered_count ?? ovm?.answered_count ?? m?.answered_count ?? 0) || 0,
        total_count:
          Number(
            runtime?.total_count ??
              ovm?.total_count ??
              m?.total_count ??
              m?.totalSteps ??
              m?.stepsCount ??
              ovm?.totalSteps ??
              ovm?.stepsCount ??
              0
          ) || 0,
        patterns_identified: patternsIdentified,
        reports_available: reportsAvailable,
        confidence_level: confidenceLevel,
        last_saved_at: asString(runtime?.last_saved_at, asString(ovm?.last_saved_at, '')),
        completed_at: asString(runtime?.completed_at, asString(ovm?.completed_at, '')),

        progress: progressData.progress,
        moduleProgress: progressData.moduleProgress,

        coach_resolution: coachResolution,
        typing_mode: typingMode,

        track_policy: tracksInfo.track_policy,
        default_track_id: tracksInfo.default_track_id,
        tracks: tracksInfo.tracks,

        content: [],
      };
    })
    .filter(Boolean);

  const finalModules = merged.length
    ? merged
    : ovModules.map((m) => {
        const module_id = normalizeModuleId(m);
        const tracksInfo = normalizeTracks(m);
        const runtime = findRunOverviewForModule(
          runMap,
          module_id,
          tracksInfo.default_track_id,
          tracksInfo.tracks
        );

        return buildModuleFromOverview(m, runtime, ro);
      });

  return {
    id: asString(bp?.pack_id, asString(bp?.id, asString(ov?.pack_id, packId))),
    version: asString(bp?.pack_version, asString(bp?.version, asString(ov?.pack_version, '1.0.0'))),
    title: asString(bp?.title, asString(ov?.title, 'Blueprint')),
    subtitle: asString(bp?.subtitle, ''),
    description: asString(bp?.description, asString(ov?.description, '')),
    startLabel: asString(bp?.startLabel, 'Start'),
    time: asString(bp?.time, ''),
    available_coaches: globalAvailableCoaches,
    selected_coach_id: asString(ro?.selected_coach_id).trim(),
    typing_mode: asString(ro?.typing_mode).trim() || 'fast',
    coach_resolution: normalizeCoachResolution(null, null, null, ro),
    overall: {
      progress_ratio: Number(ro?.overall?.progress_ratio ?? 0) || 0,
      answered_count: Number(ro?.overall?.answered_count ?? 0) || 0,
      total_count: Number(ro?.overall?.total_count ?? 0) || 0,
    },
    modules: finalModules,
  };
}

function normalizeAvailableCoaches(runOverview) {
  const list = Array.isArray(runOverview?.available_coaches) ? runOverview.available_coaches : [];

  return list
    .map((coach) => {
      const persona =
        coach && typeof coach === 'object' && coach.persona && typeof coach.persona === 'object'
          ? coach.persona
          : {};

      const avatar = persona.avatar && typeof persona.avatar === 'object' ? persona.avatar : {};
      const typing = persona.typing && typeof persona.typing === 'object' ? persona.typing : {};

      const id = asString(coach?.id).trim();
      if (!id) return null;

      return {
        id,
        label: asString(coach?.label, persona?.name || id),
        persona: {
          name: asString(persona?.name, coach?.label || id),
          slogan: asString(persona?.slogan, ''),
          introduction: asString(persona?.introduction, ''),
          avatar: {
            src: asString(avatar?.src, ''),
            url: asString(avatar?.url, asString(avatar?.src, '')),
            path: asString(avatar?.path, ''),
          },
          typing,
          tone: asString(persona?.tone, ''),
          style: asString(persona?.style, ''),
          energy: asString(persona?.energy, ''),
        },
      };
    })
    .filter(Boolean);
}
